import { useState } from "react";
import api, { fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const COLLECTION_LABELS = ["Front", "Back", "Left", "Right"];

const toB64 = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

// Read-only thumbnail grid used in order detail and dispute comparison.
export function PhotoThumbs({ photos, emptyLabel = "None" }) {
  const list = (photos || []).filter(Boolean);
  if (list.length === 0) return <p className="text-xs text-slate-400">{emptyLabel}</p>;
  return (
    <div className="flex flex-wrap gap-2" data-testid="photo-thumbs">
      {list.map((p, i) => {
        let url = typeof p === "string" ? p : p?.url;
        if (typeof url === "string" && !url.startsWith("http") && !url.startsWith("data:") && !url.startsWith("/")) url = `data:image/jpeg;base64,${url}`;
        return <a key={i} href={url} target="_blank" rel="noreferrer" data-testid="photo-thumb"><img src={url} alt="" className="h-16 w-16 rounded-lg border border-slate-200 object-cover hover:opacity-80" /></a>;
      })}
    </div>
  );
}

// Flatten a courier_photos object into an ordered array of {url,label} for comparison views.
export function courierList(cp) {
  const out = [];
  const coll = (cp && cp.collection) || {};
  COLLECTION_LABELS.forEach((l) => { if (coll[l]?.url) out.push({ url: coll[l].url, label: l }); });
  if (cp && cp.delivery?.url) out.push({ url: cp.delivery.url, label: "Delivery" });
  return out;
}

// Flatten the RETURN leg courier photos (collection from buyer + delivery to seller).
export function returnCourierList(cp) {
  const out = [];
  const coll = (cp && cp.return_collection) || {};
  COLLECTION_LABELS.forEach((l) => { if (coll[l]?.url) out.push({ url: coll[l].url, label: `Return ${l}` }); });
  if (cp && cp.return_delivery?.url) out.push({ url: cp.return_delivery.url, label: "Return delivery" });
  return out;
}

// Admin uploader (collection x4 + delivery x1) with replace/remove.
export default function CourierPhotos({ tid, photos, onChange, showReturn }) {
  const cp = photos || {};
  const [busy, setBusy] = useState("");
  const upload = async (group, label, file) => {
    if (!file) return;
    setBusy(`${group}:${label || ""}`);
    try {
      const image = await toB64(file);
      await api.post(`/admin/transactions/${tid}/courier-photo`, { group, label, image });
      toast.success("Photo uploaded");
      onChange && onChange();
    } catch { toast.error("Upload failed"); } finally { setBusy(""); }
  };
  const remove = async (group, label) => {
    try {
      await api.delete(`/admin/transactions/${tid}/courier-photo`, { params: { group, label } });
      toast.success("Photo removed");
      onChange && onChange();
    } catch { toast.error("Could not remove"); }
  };
  const Slot = ({ group, label, entry }) => (
    <div className="rounded-xl border border-slate-200 p-2" data-testid={`courier-slot-${group}-${(label || "delivery").toLowerCase()}`}>
      <p className="mb-1 text-xs font-semibold text-slate-600">{label || "Delivery photo"}</p>
      {entry?.url ? (
        <div className="space-y-1">
          <a href={entry.url} target="_blank" rel="noreferrer"><img src={entry.url} alt={label} className="h-24 w-full rounded-lg border border-slate-200 object-cover hover:opacity-80" data-testid="courier-photo-thumb" /></a>
          <p className="text-[10px] leading-tight text-slate-400">by {entry.by} · {fmtDateTime(entry.at)}</p>
          <div className="flex gap-2">
            <label className="cursor-pointer text-[11px] font-medium text-primary hover:underline">Replace<input type="file" accept="image/*" className="hidden" onChange={(e) => upload(group, label, e.target.files?.[0])} /></label>
            <button className="text-[11px] font-medium text-red-600 hover:underline" data-testid="courier-photo-remove" onClick={() => remove(group, label)}>Remove</button>
          </div>
        </div>
      ) : (
        <label className="flex h-24 cursor-pointer items-center justify-center rounded-lg border border-dashed border-slate-300 text-[11px] text-slate-400 hover:bg-slate-50" data-testid="courier-photo-upload">
          {busy === `${group}:${label || ""}` ? "Uploading…" : "+ Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(group, label, e.target.files?.[0])} />
        </label>
      )}
    </div>
  );
  return (
    <div className="space-y-3" data-testid="courier-photos">
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Collection photos (as packed, in front of the seller)</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {COLLECTION_LABELS.map((l) => <Slot key={l} group="collection" label={l} entry={(cp.collection || {})[l]} />)}
        </div>
      </div>
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Delivery photo (at the door / handover)</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4"><Slot group="delivery" entry={cp.delivery} /></div>
      </div>
      {showReturn && (
        <>
          <div data-testid="return-collection-photos">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">RETURN collection photos (as packed, from the buyer)</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {COLLECTION_LABELS.map((l) => <Slot key={`r-${l}`} group="return_collection" label={l} entry={(cp.return_collection || {})[l]} />)}
            </div>
          </div>
          <div data-testid="return-delivery-photos">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">RETURN delivery photo (at the seller's door / handover)</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4"><Slot group="return_delivery" entry={cp.return_delivery} /></div>
          </div>
        </>
      )}
    </div>
  );
}
